import { useEffect, useRef, useState } from 'react';
import { DEFAULT_AUDIO_SETTINGS, playCue, type AudioSettings } from '../lib/sounds';
import { setAudioSettings, useAudioSettings } from '../lib/audioSettings';

/**
 * L'interruttore dei segnali acustici, con il volume.
 *
 * Sta nella barra in alto perché è una preferenza che si cambia una volta e poi
 * si dimentica: durante una gara nessuno vuole aprire un pannello per abbassare
 * il beep. Il volume si sente subito — si prova e si regola — quindi il cursore
 * suona mentre lo si muove, invece di aspettare la prossima scansione.
 *
 * Le due azioni non hanno la stessa urgenza, e la barra le tratta diversamente:
 * **il silenziamento è un tocco solo**, sempre, perché capita di doverlo fare
 * con il transponder nell'altra mano; **il volume sta dietro un tocco in più**,
 * perché si regola una volta e poi resta lì.
 *
 * Sotto i 600px il cursore in barra non ci sta — la barra deve restare una riga
 * sola — quindi si apre una tendina con un cursore lungo, che è anche più
 * preciso da trascinare con il pollice.
 */
export function AudioToggle() {
  const settings = useAudioSettings();
  const [panelOpen, setPanelOpen] = useState(false);
  const wrap = useRef<HTMLSpanElement>(null);

  /*
   * La tendina si chiude toccando fuori o con Esc. È una tendina, non una
   * schermata: lasciarla aperta sopra la barra la farebbe sembrare piantata.
   */
  useEffect(() => {
    if (!panelOpen) return;

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setPanelOpen(false);
    }

    function onPointerDown(event: PointerEvent) {
      if (!wrap.current?.contains(event.target as Node)) setPanelOpen(false);
    }

    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('pointerdown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('pointerdown', onPointerDown);
    };
  }, [panelOpen]);

  function toggle() {
    setAudioSettings({ ...settings, enabled: !settings.enabled });
  }

  /**
   * Regolare il volume lo riattiva: chi alza il volume mentre è muto vuole
   * sentirlo, e il suono di prova glielo conferma.
   *
   * Il suono di prova non è un vezzo: senza, si sta girando una manopola al
   * buio.
   */
  function changeVolume(volume: number) {
    const next: AudioSettings = { enabled: true, volume };
    setAudioSettings(next);
    playCue('ok', next);
  }

  function resetVolume() {
    setAudioSettings(DEFAULT_AUDIO_SETTINGS);
  }

  return (
    <span className="audio-control" ref={wrap}>
      <button
        type="button"
        className="audio-control__button"
        onClick={toggle}
        aria-pressed={settings.enabled}
        aria-label={settings.enabled ? 'Disattiva i segnali acustici' : 'Attiva i segnali acustici'}
        title={settings.enabled ? 'Segnali acustici attivi' : 'Segnali acustici muti'}
      >
        <SpeakerIcon muted={!settings.enabled} />
      </button>

      <input
        type="range"
        className="audio-control__volume"
        min={0}
        max={1}
        step={0.05}
        value={settings.volume}
        disabled={!settings.enabled}
        onChange={(event) => changeVolume(Number(event.target.value))}
        onDoubleClick={resetVolume}
        aria-label="Volume dei segnali acustici"
      />

      {/*
        Questo pulsante esiste solo sotto i 600px, dove il cursore in barra è
        nascosto. Con il mouse non serve e non compare.
      */}
      <button
        type="button"
        className="audio-control__more"
        onClick={() => setPanelOpen((open) => !open)}
        aria-expanded={panelOpen}
        aria-label="Regola il volume dei segnali acustici"
        title="Volume"
      >
        <VolumeIcon />
      </button>

      {panelOpen && (
        <div className="audio-control__panel" role="group" aria-label="Volume dei segnali acustici">
          <input
            type="range"
            className="audio-control__panel-volume"
            min={0}
            max={1}
            step={0.05}
            value={settings.volume}
            disabled={!settings.enabled}
            onChange={(event) => changeVolume(Number(event.target.value))}
            onDoubleClick={resetVolume}
            aria-label="Volume dei segnali acustici"
          />
          {/*
            Il numero serve a vedere che il cursore ha preso: a orecchio, in una
            palestra, «più forte di prima» è difficile da giudicare.
          */}
          <span className="audio-control__panel-value" aria-hidden="true">
            {Math.round(settings.volume * 100)}%
          </span>
        </div>
      )}
    </span>
  );
}

function SpeakerIcon({ muted }: { muted: boolean }) {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path
        d="M2.5 6h2L7.5 3.4v9.2L4.5 10h-2z"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinejoin="round"
      />
      {muted ? (
        <path
          d="M10.5 6.2l3 3.6M13.5 6.2l-3 3.6"
          stroke="currentColor"
          strokeWidth="1.4"
          strokeLinecap="round"
        />
      ) : (
        <path
          d="M10 5.6a3.2 3.2 0 010 4.8M12.2 3.8a6 6 0 010 8.4"
          stroke="currentColor"
          strokeWidth="1.4"
          strokeLinecap="round"
        />
      )}
    </svg>
  );
}

/** Due manopole: il segno che lì dentro si regola qualcosa, non solo si accende. */
function VolumeIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path
        d="M2.5 4.5h6M11.9 4.5h1.6M2.5 11.5h2.6M8.5 11.5h5"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
      />
      <circle cx="10.2" cy="4.5" r="1.7" stroke="currentColor" strokeWidth="1.4" />
      <circle cx="6.8" cy="11.5" r="1.7" stroke="currentColor" strokeWidth="1.4" />
    </svg>
  );
}
